import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, History } from "lucide-react";
import { API } from "../../config";
import { useAuth } from "../../AuthContext";

const STATUS_COLOR = {
  ok: "#10b981",
  quota: "#f59e0b",
  invalid: "#f43f5e",
  missing: "#94a3b8",
  error: "#ef4444",
  unknown: "#64748b",
};

export default function HealthHistoryCard({ dm }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { authHeader } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/health/history?days=30`, { headers: authHeader() });
      setData(res.data);
    } catch { /* noop */ }
    setLoading(false);
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const text = dm ? "text-slate-100" : "text-[#191c1d]";
  const muted = dm ? "text-slate-500" : "text-[#6b7a7d]";
  const windowMs = data ? new Date(data.to) - new Date(data.from) : 1;

  return (
    <div className={`mt-6 rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`}
      style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}
      data-testid="health-history-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <History className={`w-5 h-5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
          <h3 className={`editorial-heading text-xl font-medium ${text}`}>Provider Health — last 30 days</h3>
        </div>
        <button onClick={load} className={muted} title="Refresh" data-testid="health-history-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {!data ? (
        <p className={`text-sm text-center py-8 ${muted}`}>{loading ? "Loading…" : "No history yet"}</p>
      ) : (
        <div className="space-y-4">
          {(data.providers || []).map((p) => (
            <div key={p.name} data-testid={`health-history-row-${p.name}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-sm font-medium ${text}`}>{p.label}</span>
                <span className={`text-xs font-semibold`} style={{ color: STATUS_COLOR[p.current] || STATUS_COLOR.unknown }}>
                  {p.current}
                </span>
              </div>
              <div className="flex h-3 rounded-full overflow-hidden" style={{ background: dm ? "#0f1516" : "#eef0f1" }}>
                {(p.segments || []).map((s, i) => {
                  const w = Math.max(0.4, ((new Date(s.to) - new Date(s.from)) / windowMs) * 100);
                  return (
                    <div
                      key={i}
                      style={{ width: `${w}%`, background: STATUS_COLOR[s.status] || STATUS_COLOR.unknown }}
                      title={`${s.status} — ${new Date(s.from).toLocaleString()} → ${new Date(s.to).toLocaleString()}`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
          <div className={`flex flex-wrap gap-4 pt-2 text-xs ${muted}`}>
            {Object.entries(STATUS_COLOR).filter(([k]) => k !== "unknown").map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: v }} /> {k}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
